import { createEnv } from '@t3-oss/env-core'
import { FeatureFlagProvider } from '@thyme/libs/src/flags/types'
import { envSchema } from '@/envSchema'
import type { Config } from '@/legacy/types/api/config'

export type ValidatedEnv = ReturnType<typeof parseEnv>

/**
 * Validates a raw env record against {@link envSchema} and throws if any
 * var is missing or invalid. Exposed as a pure function so tests can pass
 * in the output of `loadEnv(mode, ...)` for each deploy mode.
 * @param runtimeEnv
 */
export function parseEnv(runtimeEnv: Record<string, string | undefined>) {
  return createEnv({
    clientPrefix: 'VITE_',
    client: envSchema,
    runtimeEnv,
  })
}

/**
 * Pure flat→nested mapping from validated env vars to the legacy Config shape.
 * When adding a new env var, add it to envSchema.ts AND map it here.
 * The envToConfig.spec.ts snapshot test will catch any missing mappings.
 * @param parsed
 */
export function buildConfig(parsed: ValidatedEnv): Config {
  return {
    env: parsed.VITE_ENV,
    apiUrl: parsed.VITE_API_URL,
    auth0: {
      domain: parsed.VITE_AUTH0_DOMAIN,
      clientId: parsed.VITE_AUTH0_CLIENT_ID,
      organizationId:
        parsed.VITE_AUTH0_ORGANIZATION_ID === 'none'
          ? null
          : parsed.VITE_AUTH0_ORGANIZATION_ID,
      apiAudience: parsed.VITE_AUTH0_API_AUDIENCE,
    },
    connect: {
      url: parsed.VITE_CONNECT_URL,
    },
    flags: {
      primaryProvider:
        parsed.VITE_FLAGS_PRIMARY_PROVIDER as FeatureFlagProvider,
      launchDarklyClientSideId: parsed.VITE_FLAGS_LAUNCH_DARKLY_CLIENT_SIDE_ID,
    },
    textingSubtasksUnknownNumberPatientId:
      parsed.VITE_TEXTING_SUBTASKS_UNKNOWN_NUMBER_PATIENT_ID,
    datadog: {
      enabled: parsed.VITE_DATADOG_ENABLED,
      applicationId: parsed.VITE_DATADOG_APPLICATION_ID,
      clientToken: parsed.VITE_DATADOG_CLIENT_TOKEN,
      service: parsed.VITE_DATADOG_SERVICE,
      site: parsed.VITE_DATADOG_SITE,
      env: parsed.VITE_DATADOG_ENV,
    },
    amazonCCP: {
      thymecareInstanceUrl: parsed.VITE_AMAZON_CCP_THYMECARE_INSTANCE_URL,
      instanceRegion: parsed.VITE_AMAZON_CCP_INSTANCE_REGION,
      telemetryPingSubdomain: parsed.VITE_AMAZON_CCP_TELEMETRY_PING_SUBDOMAIN,
      thymecareLoginUrl: parsed.VITE_AMAZON_CCP_THYMECARE_LOGIN_URL,
    },
    strapi: {
      apiUrl: parsed.VITE_STRAPI_API_URL,
      apiKey: parsed.VITE_STRAPI_API_KEY,
    },
    meiliSearch: {
      url: parsed.VITE_MEILI_SEARCH_URL,
      apiKey: parsed.VITE_MEILI_SEARCH_API_KEY,
    },
    eventService: {
      url: parsed.VITE_EVENT_SERVICE_URL,
      apiKey: parsed.VITE_EVENT_SERVICE_API_KEY,
    },
  }
}

export const env = parseEnv(import.meta.env)
export const envToConfig = (): Config => buildConfig(env)
